import { useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import SurfaceCard from './SurfaceCard';
import Eyebrow from './Eyebrow';

export interface MetricPoint {
  time: number;
  [containerId: string]: number;
}

/**
 * A metric as a line over time: the name, the reading, and the plot — the same
 * shape as the Agent overview's CPU and Memory cards.
 *
 * Drawn as inline SVG rather than through recharts. The chart needs a
 * polyline, an area fill, three gridlines and a crosshair; recharts is ~70KB
 * gzipped for that, and the axis labels have to live in HTML anyway (see
 * below).
 *
 * Each row of `data` carries the reading of exactly one container — the series
 * builder pushes one row per sample, it does not merge them by timestamp — so
 * the rows are grouped by container id and each one gets its own line in its
 * own colour. That is the reading the old graphs drew, and the only one that
 * stays honest as containers come and go mid-window.
 */

// Fixed coordinate space; CSS sizes the SVG. The X axis is stretched to the
// card width (`preserveAspectRatio="none"`), which is why the labels are HTML
// and not <text>: SVG text would stretch with it.
const CHART_W = 320;
const CHART_H = 140;
const PAD_TOP = 12;
// Only enough room for the baseline's own tick label to sit on it without
// clipping the bottom of the box. The clock labels used to live in here too,
// which is what the baseline kept landing on.
const PAD_BOTTOM = 10;

/** Width of the Y label gutter. The clock row below the plot is inset by the
 *  same amount so both axes start at the same x. */
const Y_GUTTER = 42;

const clock = (ms: number) =>
  new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });

/** One row per container: `{ time, [id]: value }` → `{ id: [{time, value}] }`. */
const toSeries = (data: MetricPoint[]) => {
  const byContainer = new Map<string, { time: number, value: number }[]>();

  data.forEach(row => {
    Object.entries(row).forEach(([key, value]) => {
      if (key === 'time' || typeof value !== 'number') {
        return;
      }
      const points = byContainer.get(key) || [];
      points.push({ time: row.time, value });
      byContainer.set(key, points);
    });
  });

  byContainer.forEach(points => points.sort((a, b) => a.time - b.time));
  return byContainer;
};

const MetricChart = ({ label, data, color, colors, yMax, format, unit, height = 140, timeWindow, emptyAsZero, isLoading, errorMessage, emptyLabel = 'No data for this window.' }: {
  label: React.ReactNode,
  data?: MetricPoint[],
  /** The metric's own hue, used while there is one container to draw. */
  color: string,
  /** One colour per container id, as the series builder assigned them. Read
   *  only once a second container appears: telling two lines apart is worth
   *  more then than the metric's hue is. */
  colors?: { [containerId: string]: string },
  /** Top of the Y axis where the metric has a ceiling — the plan limit for CPU,
   *  RAM and processes. Left out, the peak of the window sets it. */
  yMax?: number,
  /** Y tick labels and the reading in the head, so both read in one unit. */
  format: (value: number) => string,
  /** Dim suffix after the reading, for the units that do not fit in `format`. */
  unit?: string,
  /** Plot height in px. The viewBox is fixed and the SVG stretches into this,
   *  so a shorter card squashes the same drawing rather than cropping it —
   *  the Overview runs shorter than the Metrics screen, where the chart is
   *  the whole point of the page. */
  height?: number,
  /** The span the caller subscribed for. Only read when there are no samples
   *  and `emptyAsZero` is on — a chart of nothing still has to label an X
   *  axis, and this is the window it is drawing. */
  timeWindow?: number,
  /** Draw an empty window as a flat zero against the ceiling instead of a line
   *  of text. On the Overview the chart is one of three cards read at a
   *  glance, and the headroom is the thing worth seeing even when nothing is
   *  using it. Off by default, and off on the Metrics screen: that page is
   *  where "not measured" and "measured zero" are exactly the distinction you
   *  came to make. */
  emptyAsZero?: boolean,
  isLoading?: boolean,
  errorMessage?: string,
  emptyLabel?: string,
}) => {
  const [hoveredTime, setHoveredTime] = useState<number | null>(null);

  const series = toSeries(data || []);
  const all = Array.from(series.values()).flat().sort((a, b) => a.time - b.time);
  // Having samples is what makes this a chart. A container that sat at zero
  // CPU for the whole window HAS data — reporting that as "no data" was the
  // difference between "idle" and "not measured".
  const hasData = all.length > 0;

  // An empty window drawn as zero rather than as a sentence. There is no
  // first and last sample to take the axis from, so the window the caller
  // subscribed for stands in and the line runs the width of it.
  const isZeroed = !hasData && !!emptyAsZero && !isLoading && !errorMessage;
  const now = Date.now();

  const from = hasData ? all[0].time : now - (timeWindow || 60 * 60 * 1000);
  const to = hasData ? all[all.length - 1].time : now;
  const span = Math.max(to - from, 1);
  const peak = hasData ? Math.max(...all.map(point => point.value)) : 0;
  // A flat-at-zero window still needs a scale, or every point lands on the
  // baseline and the top label reads "0" twice.
  const top = yMax || peak || 1;

  const x = (time: number) => ((time - from) / span) * CHART_W;
  const y = (value: number) =>
    PAD_TOP + (1 - Math.max(0, Math.min(1, value / top))) * (CHART_H - PAD_TOP - PAD_BOTTOM);

  // Top, middle, baseline. Three read cleanly at this height.
  const ticks = [top, top / 2, 0];

  // The reading in the head: the newest sample, or the one under the pointer.
  const hovered = hoveredTime === null
    ? null
    : all.reduce((closest, point) =>
      Math.abs(point.time - hoveredTime) < Math.abs(closest.time - hoveredTime) ? point : closest);
  const reading = hovered
    || (hasData ? all[all.length - 1] : null)
    || (isZeroed ? { time: to, value: 0 } : null);

  const onPointerMove = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (!hasData) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    setHoveredTime(from + fraction * span);
  };

  return (
    <SurfaceCard className="p-4 lg:px-[18px] flex flex-col gap-4">
      <div className="flex items-baseline justify-between gap-3">
        <Eyebrow>{label}</Eyebrow>
        {reading ? (
          <span className="flex items-baseline gap-1.5 font-sora text-ds-20 font-semibold tracking-[-0.02em] text-white">
            {format(reading.value)}
            {unit ? <span className="font-courrier text-ds-115 font-normal uppercase tracking-[0.06em] text-navy-300">{unit}</span> : null}
            {/* Under the pointer the head reads that sample instead of the
                newest one, so it has to say which moment it is reading. */}
            {hovered ? <span className="font-courrier text-ds-115 font-normal text-navy-300">at {clock(hovered.time)}</span> : null}
          </span>
        ) : null}
      </div>

      {/* Nothing to plot costs a line, not a plot's worth of height. Holding
          the full 140px open to print "no data" is what made a board of these
          cards read as empty boxes — the state is one short sentence and it
          should take the room of one. */}
      {isLoading ? (
        <div className="text-ds-135 text-navy-300">Loading…</div>
      ) : errorMessage ? (
        <div className="text-ds-135 text-state-err">{errorMessage}</div>
      ) : !hasData && !isZeroed ? (
        <div className="text-ds-135 text-navy-300">{emptyLabel}</div>
      ) : (
        <div className="flex flex-col gap-1">
          <div className="relative flex items-stretch" style={{ height }}>
            {/* The Y labels sit in a gutter of their own rather than floating on
                the plot, so the digits line up and never cross a line. */}
            <div className="relative flex-none" style={{ width: Y_GUTTER }}>
              {ticks.map((value, i) => (
                <span
                  key={i}
                  className="absolute right-1.5 -translate-y-1/2 font-courrier text-[9px] leading-none text-navy-300 whitespace-nowrap"
                  style={{ top: `${(y(value) / CHART_H) * 100}%` }}
                >
                  {format(value)}
                </span>
              ))}
            </div>

            <div className="relative flex-1 min-w-0">
              <svg
                className="w-full h-full block cursor-crosshair"
                viewBox={`0 0 ${CHART_W} ${CHART_H}`}
                preserveAspectRatio="none"
                onPointerMove={onPointerMove}
                onPointerLeave={() => setHoveredTime(null)}
              >
                {ticks.map((value, i) => (
                  <line
                    key={i}
                    x1={0}
                    x2={CHART_W}
                    y1={y(value)}
                    y2={y(value)}
                    stroke="#283A58"
                    strokeWidth={1}
                    strokeDasharray={i === ticks.length - 1 ? undefined : '2 4'}
                    opacity={i === ticks.length - 1 ? 1 : 0.55}
                    vectorEffect="non-scaling-stroke"
                  />
                ))}

                {Array.from(series.entries()).map(([containerId, points]) => {
                  const stroke = series.size > 1
                    ? ((colors && colors[containerId]) || color)
                    : color;
                  const line = points.map(point => `${x(point.time).toFixed(2)},${y(point.value).toFixed(2)}`);
                  // The line, then back down to the baseline and closed.
                  const baseline = y(0).toFixed(2);
                  const area = `M ${line.join(' L ')} L ${x(points[points.length - 1].time).toFixed(2)},${baseline} L ${x(points[0].time).toFixed(2)},${baseline} Z`;

                  return (
                    <g key={containerId}>
                      <path d={area} fill={stroke} opacity={0.12} />
                      <polyline
                        points={line.join(' ')}
                        fill="none"
                        stroke={stroke}
                        strokeWidth={1.5}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        vectorEffect="non-scaling-stroke"
                      />
                      {/* A polyline of one point draws nothing at all, and a
                          container with a single sample so far is still a
                          reading. It gets a dot. */}
                      {points.length === 1 ? (
                        <circle cx={x(points[0].time)} cy={y(points[0].value)} r={2} fill={stroke} />
                      ) : null}
                    </g>
                  );
                })}

                {/* No series, so nothing above would have drawn a line. The
                    baseline is the reading. */}
                {isZeroed ? (
                  <polyline
                    points={`${x(from).toFixed(2)},${y(0).toFixed(2)} ${x(to).toFixed(2)},${y(0).toFixed(2)}`}
                    fill="none"
                    stroke={color}
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                ) : null}

                {hovered ? (
                  <line
                    x1={x(hovered.time)}
                    x2={x(hovered.time)}
                    y1={PAD_TOP}
                    y2={CHART_H - PAD_BOTTOM}
                    stroke="#8FA2BD"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    opacity={0.7}
                    vectorEffect="non-scaling-stroke"
                  />
                ) : null}
              </svg>
            </div>
          </div>

          {/* The clock is a row under the plot, not three labels floating in the
              bottom of it. Inside the box they were laid over whatever the line
              was doing down there, and an idle window — the line flat on the
              baseline — printed the times straight through it.

              First, middle and last of the WINDOW rather than of the samples: a
              gap at either end is a fact about the run. */}
          <div className="flex" style={{ paddingLeft: Y_GUTTER }}>
            <div className="relative flex-1 min-w-0 h-3">
              <span className="absolute top-0 left-0 font-courrier text-ds-105 leading-none text-navy-300">{clock(from)}</span>
              <span className="absolute top-0 left-1/2 -translate-x-1/2 font-courrier text-ds-105 leading-none text-navy-300">
                {clock(from + span / 2)}
              </span>
              <span className="absolute top-0 right-0 font-courrier text-ds-105 leading-none text-navy-300">{clock(to)}</span>
            </div>
          </div>
        </div>
      )}
    </SurfaceCard>
  );
};

export default MetricChart;
